import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Avatar, 
  Container,
  Alert,
  CircularProgress,
  Link,
  Divider
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import api from '../api';
import { useLanguage } from '../context/LanguageContext';
import { appNameWithVersion } from '../version';
import logger from './../utils/logger.js';

const Signup = ({ onSignupSuccess, onSwitchToLogin }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert username to lowercase automatically
    const processedValue = name === 'username' ? value.toLowerCase() : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return false;
    }

    if (!formData.password.trim()) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api.post('/signup', {
        email: formData.email.trim().toLowerCase(),
        username: formData.username.trim(),
        password: formData.password,
        display_name: formData.displayName.trim() || formData.username.trim()
      });
      
      if (response.data.success) {
        setSuccess(response.data.message);
        
        // Call success callback after a short delay to show success message
        setTimeout(() => {
          if (onSignupSuccess) {
            onSignupSuccess({
              message: response.data.message,
              user: response.data.user
            });
          }
        }, 2000);
      } else {
        setError(response.data.message || 'Signup failed. Please try again.');
      }
    } catch (err) {
      logger.error('Signup error:', err);
      const errorMessage = err.response?.data?.message || 'Signup failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3} 
        sx={{ 
          mt: 8, 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}
      >
        <Box 
          component="img" 
          src="/salinator-logo.png" 
          alt="Salinator Logo"
          sx={{ 
            maxWidth: '154px', 
            width: '70%', 
            mb: 2 
          }}
        />
        
        <Box 
          component="img" 
          src="/compatible-logo.png" 
          alt="Compatible Logo"
          sx={{ 
            maxWidth: '154px',
            width: '70%', 
            mb: 3 
          }}
        />
        
        <Typography 
          variant="h6" 
          component="h2" 
          color="primary" 
          sx={{ mb: 2, fontWeight: 'bold' }}
        >
          {appNameWithVersion}
        </Typography>
        
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <PersonAddIcon />
        </Avatar>
        
        <Typography component="h1" variant="h5">
          {language === 'nl' ? 'Account Aanmaken' : 'Create Account'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={language === 'nl' ? 'E-mailadres' : 'Email Address'}
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            placeholder={language === 'nl' ? 'name@company.com' : 'name@company.com'}
            helperText={language === 'nl' ? 'Gebruik uw zakelijke e-mailadres' : 'Use your business email address'}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={language === 'nl' ? 'Gebruikersnaam' : 'Username'}
            name="username"
            autoComplete="username"
            value={formData.username}
            onChange={handleChange}
            disabled={loading}
            placeholder={language === 'nl' ? 'gebruikersnaam' : 'username'}
            helperText={language === 'nl' ? 'Kies een unieke gebruikersnaam (wordt automatisch omgezet naar kleine letters)' : 'Choose a unique username (will be automatically converted to lowercase)'}
          />
          
          <TextField
            margin="normal"
            fullWidth
            id="displayName"
            label={language === 'nl' ? 'Volledige Naam (Optioneel)' : 'Full Name (Optional)'}
            name="displayName"
            autoComplete="name"
            value={formData.displayName}
            onChange={handleChange}
            disabled={loading}
            placeholder={language === 'nl' ? 'Uw volledige naam' : 'Your full name'}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={language === 'nl' ? 'Wachtwoord' : 'Password'}
            type="password"
            id="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
            placeholder={language === 'nl' ? 'Wachtwoord' : 'Password'}
            helperText={language === 'nl' ? 'Minimaal 6 karakters' : 'Minimum 6 characters'}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label={language === 'nl' ? 'Bevestig Wachtwoord' : 'Confirm Password'}
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={formData.confirmPassword}
            onChange={handleChange}
            disabled={loading}
            placeholder={language === 'nl' ? 'Bevestig wachtwoord' : 'Confirm password'}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              language === 'nl' ? 'Account Aanmaken' : 'Create Account'
            )}
          </Button>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {language === 'nl' ? 'OF' : 'OR'}
            </Typography>
          </Divider>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {language === 'nl' ? 'Heeft u al een account?' : 'Already have an account?'}
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                if (onSwitchToLogin) {
                  onSwitchToLogin();
                }
              }}
              sx={{ mt: 1, textDecoration: 'underline', cursor: 'pointer' }}
            >
              {language === 'nl' ? 'Inloggen' : 'Sign In'}
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Signup; 